import type { Swiper as SwiperClass } from 'swiper/types';

import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { clsx } from 'clsx';
import Image from 'next/image';
import { EffectFade, FreeMode, Pagination, Thumbs } from 'swiper/modules';

import { useLightBoxModal } from '@screens/Product/hooks/useLightBoxModal';
import { PRODUCT_IMAGE_PATH } from '@shared/config/paths/paths';
import { ICON_DEFAULT } from '@shared/constants/colors';
import { ID_CLIENTS_PHOTO_ATTRIBUTE, ID_PRODUCT_IMAGE_ATTRIBUTE } from '@shared/constants/keys';
import { useAppSelector } from '@shared/hooks/redux';
import { HoverMask } from '@shared/ui/HoverMask/HoverMask';
import { getViewPort } from '@shared/ui/ScreenSizeTracking';
import { HStack, VStack } from '@shared/ui/Stack';
import { LightBoxModal } from '@widgets/LightBoxModal';

import cls from './Slider.module.scss';

const MAX_LEN_CAROUSEL = 5;

export const Slider = () => {
  const viewPort = useAppSelector(getViewPort);
  const productImages = useAppSelector((state) => state.product.data.props[ID_PRODUCT_IMAGE_ATTRIBUTE]?.values);
  const clientImages = useAppSelector((state) => state.product.data.props[ID_CLIENTS_PHOTO_ATTRIBUTE]?.values);
  const name = useAppSelector((state) => state.product.data.name);

  const [swiper, setSwiper] = useState<null | SwiperClass>(null);
  const [thumbsSwiper, setThumbsSwiper] = useState<null | SwiperClass>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(0);

  const images = [...(productImages || []), ...(clientImages || [])];

  const isDesktop = viewPort === 'desktop';
  const isMobile = viewPort === 'mobile';

  const { openLightBoxModal, currentImgUrl, imageUrls, handleCloseLightBox, handleChangeCurrentImageUrl } =
    useLightBoxModal({ values: images });

  return (
    <div className={cls.mainWrap}>
      <HStack className={cls.wrap}>
        <div className={cls.main}>
          {images.length > 0 ? (
            <Swiper
              loop={images.length > MAX_LEN_CAROUSEL}
              effect="fade"
              spaceBetween={10}
              className={cls.mainSlider}
              onSwiper={setSwiper}
              thumbs={{ swiper: thumbsSwiper }}
              modules={[FreeMode, Thumbs, EffectFade, Pagination]}
              pagination={{ clickable: true }}
              onSlideChange={(api) => {
                setActiveIndex(api.realIndex);
              }}
              speed={300}
            >
              {imageUrls.map((src, i) => (
                <SwiperSlide
                  key={src}
                  className={cls.mainSlide}
                  onClick={handleChangeCurrentImageUrl(src)}
                >
                  <HoverMask mobileHidden />
                  <Image
                    src={src}
                    alt={`${name}-${i}`}
                    fill
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          ) : // <div className={cls.preview}>preview</div>
          null}
        </div>
        {images && images.length ? (
          <VStack className={cls.thumb}>
            {images && images.length > MAX_LEN_CAROUSEL ? (
              <button
                aria-label={'Показать предыдущее изображение'}
                onClick={() => swiper?.slidePrev()}
                className={cls.prev}
              >
                <ChevronUp
                  size={24}
                  color={ICON_DEFAULT}
                />
              </button>
            ) : null}
            <Swiper
              className={cls.thumbSlider}
              direction={isDesktop ? 'vertical' : 'horizontal'}
              onSwiper={setThumbsSwiper}
              spaceBetween={12}
              slidesPerView={isDesktop ? 5 : 'auto'}
              freeMode
              speed={300}
              modules={[FreeMode, Thumbs]}
            >
              {images.map((image, index) => (
                <SwiperSlide
                  key={image.value}
                  className={clsx(cls.thumbSlide, { [cls.active]: index === activeIndex })}
                >
                  <Image
                    className={cls.thumbImg}
                    src={`${PRODUCT_IMAGE_PATH}/${image.value}/600.webp`}
                    alt={''}
                    fill
                  />
                </SwiperSlide>
              ))}
            </Swiper>
            {images && images.length > MAX_LEN_CAROUSEL ? (
              <button
                aria-label={'Показать следующее изображение'}
                onClick={() => swiper?.slideNext()}
                className={cls.next}
              >
                <ChevronDown
                  size={24}
                  color={ICON_DEFAULT}
                />
              </button>
            ) : null}
          </VStack>
        ) : null}
        {!isMobile && currentImgUrl && openLightBoxModal && (
          <LightBoxModal
            open={openLightBoxModal}
            onClose={handleCloseLightBox}
            currentImageUrl={currentImgUrl}
            imageUrls={imageUrls}
          />
        )}
      </HStack>
    </div>
  );
};
